import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { LangSelect } from "cerulean";
import { ContentCard } from "@narmi/design_system";
import type MembershipType from "byzantine/src/Membership";
import Organization from "byzantine/src/Organization";
import { useCurrentUser } from "../../../contexts/CurrentUserContext";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import { LangSelectContext } from "../../../../components/AppLocalizationProvider";
import EstatementsManager from "../../../estatements/EstatementsManager";
import LinkedAccountMembershipsCard from "../linked_account_memberships";
import PasswordInfoCard from "../PasswordInfoCard";
import BusinessInfoCard from "./BusinessInfoCard";
import ContactInfoCard from "./ContactInfoCard";
import EditDialog from "./EditDialog";
import useIsProfileEditDialogOpen from "./useIsProfileEditDialogOpen";
import styles from "./UserSettingsProfilePage.module.scss";
import IncomeReportingProfileCard from "../../../../src/modules/incomeReporting/components/IncomeReportingProfileCard";
import AccessibilityPreferencesCard from "./AccessibilityPreferencesCard";

interface UserSettingsProfilePageProps {
  memberships: MembershipType[];
}

const UserSettingsProfilePage = ({
  memberships,
}: UserSettingsProfilePageProps) => {
  const { l10n } = useLocalization();
  const {
    showLangSelect,
    localeCodes,
    localeNames,
    selectedLocale,
    setSelectedLocale,
  } = useContext(LangSelectContext);
  const userFeatures: {
    account_membership?: boolean; // eslint-disable-line camelcase
  } = useUserFeatures();
  const { currentUser } = useCurrentUser();
  const isBusinessUser = !!currentUser?.isBusiness();
  const navigate = useNavigate();
  const isDialogOpen = useIsProfileEditDialogOpen();
  const [organization, setOrganization] = useState<Organization>();

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!currentUser?.org_uuid) {
        return;
      }
      const response = await Organization.getOrganization(
        currentUser?.org_uuid,
      );
      setOrganization(response);
    };

    if (isBusinessUser) {
      fetchOrganization();
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <ContactInfoCard />
        {isBusinessUser && <BusinessInfoCard organization={organization} />}
        {showLangSelect && (
          <ContentCard kind="elevated">
            <h4 className="fontFamily--default fontSize--heading4 margin--bottom">
              {l10n.getString("heading-language")}
            </h4>
            <LangSelect
              localeCodes={localeCodes}
              localeNames={localeNames}
              selectedLocale={selectedLocale ?? ""}
              onChange={(lang: string) => {
                setSelectedLocale(lang);
              }}
            />
          </ContentCard>
        )}
        {userFeatures?.account_membership && (
          <LinkedAccountMembershipsCard initialMemberships={memberships} />
        )}
        <PasswordInfoCard />
        <IncomeReportingProfileCard />
      </div>
      <div className={styles.column}>
        <EstatementsManager />
        <AccessibilityPreferencesCard />
      </div>
      <EditDialog
        isOpen={isDialogOpen}
        closeDialog={() => navigate("/profile")}
        setOrganization={setOrganization}
        organization={organization}
      />
    </div>
  );
};

export default UserSettingsProfilePage;
